import { gql } from '@apollo/client';
import { ROUTE_FIELDS, ROUTE_ORDER_FIELDS } from '@/graphql/queries/routes';
import { STOP_FIELDS } from '@/graphql/queries/stops';
import { ORDER_FIELDS, ORDER_DISPATCH_SCALARS } from '@/graphql/queries/orders';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { TEAMMATE_FIELDS } from '@/graphql/queries/teammates';
import { CLIENT_DISPATCH_SETTINGS_FIELDS } from '@/graphql/fragments/client_dispatch_settings';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';

export const ASSIGN_ORDERS_UPSERT = gql`
    ${ROUTE_FIELDS}
    ${STOP_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    ${ORDER_DISPATCH_SCALARS}
    mutation AssignOrders(
        $route_ids: [uuid!]!
        $events: [order_wh_events_insert_input!]!
        $order_updates: [orders_updates!]!
        $routes: [routes_insert_input!]!
        $stops: [stops_insert_input!]!
        $mappings_insert: [order_route_mappings_insert_input!] = []
        $mapping_ids_delete: [uuid!] = []
    ) {
        delete_stops(where: { route_id: { _in: $route_ids } }) {
            affected_rows
        }
        delete_order_route_mappings(where: { mapping_id: { _in: $mapping_ids_delete } }) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }
        # No stopsByRouteId here: this root resolves after delete_stops but
        # before insert_stops in Hasura's sequential transaction, so it would
        # return the empty mid-transaction stop set. insert_stops.returning
        # below is the authoritative post-assign stop state.
        insert_routes(objects: $routes, on_conflict: { constraint: routes_pkey, update_columns: [need_to_optimize] }) {
            returning {
                ...RouteFields
            }
        }
        insert_stops(objects: $stops) {
            affected_rows
            returning {
                ...StopFields
            }
        }
        # Events insert before the orders update so that if events are ever
        # passed, update_orders_many.returning reflects post-event trigger and
        # computed-field state (matching UPDATE_ROUTE_STOPS' root ordering).
        insert_order_wh_events(objects: $events) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }
        # update_orders_many executes one UPDATE...RETURNING per block, and
        # returning the giant OrderFields fragment per block was the ~130ms/order
        # bottleneck (7 s for 50 orders). OrderDispatchScalars is the union of
        # every caller's _set — scalars only, no joins — so the response is
        # authoritative for the columns this write touches. The rest of
        # OrderFields stays decoupled into a single bulk orders(...) query after
        # the mutation completes (~40ms for the same data).
        update_orders_many(updates: $order_updates) {
            affected_rows
            returning {
                ...OrderDispatchScalars
            }
        }
        insert_order_route_mappings(objects: $mappings_insert) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }
    }
`;

export const UPDATE_ORDERS = gql`
    ${ORDER_FIELDS}

    mutation UpdateOrders($order_ids: [uuid!]!, $update: orders_set_input!) {
        update_orders_many(updates: { where: { order_id: { _in: $order_ids } }, _set: $update }) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
    }
`;

export const UPDATE_ORDERS_MANY = gql`
    ${ORDER_FIELDS}

    mutation UpdateOrdersMany($updates: [orders_updates!] = []) {
        update_orders_many(updates: $updates) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
    }
`;

export const UPDATE_ROUTE_STOPS = gql`
    ${STOP_FIELDS}
    ${ROUTE_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    ${ORDER_DISPATCH_SCALARS}

    mutation UpdateStops(
        $route_id: uuid!
        $route_update: routes_set_input!
        $order_updates: [orders_updates!] = []
        $updates: [stops_updates!] = []
        $inserts: [stops_insert_input!] = []
        $deletes: [uuid!] = []
        $events: [order_wh_events_insert_input!] = []
        $mappings_insert: [order_route_mappings_insert_input!] = []
        $mapping_ids_delete: [uuid!] = []
    ) {
        delete_order_route_mappings(where: { mapping_id: { _in: $mapping_ids_delete } }) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }

        insert_order_wh_events(objects: $events) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }

        # OrderDispatchScalars, not OrderFields: the fat fragment re-executed its
        # whole subtree once per update block and blew Hasura's 60s limit on
        # large routes (ENG-9527). The narrow fragment is the union of every
        # caller's _set — scalars only, no joins — so the server stays
        # authoritative over these columns for the price of the UPDATE itself.
        update_orders_many(updates: $order_updates) {
            affected_rows
            returning {
                ...OrderDispatchScalars
            }
        }

        update_stops_many(updates: $updates) {
            affected_rows
            returning {
                ...StopFields
            }
        }

        insert_stops(objects: $inserts) {
            affected_rows
            returning {
                ...StopFields
            }
        }

        delete_stops(where: { stop_id: { _in: $deletes } }) {
            affected_rows
            returning {
                ...StopFields
            }
        }

        insert_order_route_mappings(objects: $mappings_insert) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }

        # Resolves LAST in Hasura's sequential single-transaction execution, so
        # this snapshots the authoritative post-mutation stop set and mapping
        # list. The replace merges on routes.stopsByRouteId / routes.orders
        # (src/graphql/cache.js) then maintain both lists on every caller — no
        # update() needed for them. NOTE: the order_by arg makes this write the
        # stopsByRouteId({"order_by":{"ordering":"asc"}}) storage key (the one
        # QUERY_PLANNING_DATA reads); the argument-less stopsByRouteId key used
        # by OrderDetailsPage/CustomerTrack/RouteDetails documents is NOT
        # updated here — those pages reconcile via their own refetch or
        # subscription. order { order_id } is only a ref anchor; if the order
        # row is not already cached, Apollo creates a skeletal entity and any
        # richer watcher self-heals with a network refetch.
        update_routes_by_pk(pk_columns: { route_id: $route_id }, _set: $route_update) {
            ...RouteFields
            stopsByRouteId(order_by: { ordering: asc }) {
                ...StopFields
            }
            orders {
                ...OrderRouteMappings
                order {
                    order_id
                }
            }
        }
    }
`;

// Twin of UPDATE_ROUTE_STOPS for the plan-page auto-remove flow
// (useAssignToRoute.unassign). The selection sets are now identical — both
// return OrderDispatchScalars from update_orders_many — and only the operation
// name differs, which keeps the two flows distinguishable in Hasura logs and
// Apollo devtools. Kept separate for that reason alone; if you change one
// document's shape, change both.
//
// The freed orders' remaining OrderFields data still arrives via one bulk
// orders(...) SELECT after the mutation (FETCH_ORDERS_BY_IDS in onCompleted);
// patchPlanningDataOnUnassign re-buckets from the returning above.
export const UNASSIGN_ORDERS_UPDATE = gql`
    ${STOP_FIELDS}
    ${ROUTE_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    ${ORDER_DISPATCH_SCALARS}

    mutation UnassignOrders(
        $route_id: uuid!
        $route_update: routes_set_input!
        $order_updates: [orders_updates!] = []
        $updates: [stops_updates!] = []
        $inserts: [stops_insert_input!] = []
        $deletes: [uuid!] = []
        $events: [order_wh_events_insert_input!] = []
        $mappings_insert: [order_route_mappings_insert_input!] = []
        $mapping_ids_delete: [uuid!] = []
    ) {
        delete_order_route_mappings(where: { mapping_id: { _in: $mapping_ids_delete } }) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }

        insert_order_wh_events(objects: $events) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }

        # OrderDispatchScalars covers every column this doc's _set writes, so the
        # board paints server-confirmed values straight from the response. The
        # remaining OrderFields data is still decoupled into one bulk orders(...)
        # query (FETCH_ORDERS_BY_IDS) after the mutation completes.
        update_orders_many(updates: $order_updates) {
            affected_rows
            returning {
                ...OrderDispatchScalars
            }
        }

        update_stops_many(updates: $updates) {
            affected_rows
            returning {
                ...StopFields
            }
        }

        insert_stops(objects: $inserts) {
            affected_rows
            returning {
                ...StopFields
            }
        }

        delete_stops(where: { stop_id: { _in: $deletes } }) {
            affected_rows
            returning {
                ...StopFields
            }
        }

        insert_order_route_mappings(objects: $mappings_insert) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }

        update_routes_by_pk(pk_columns: { route_id: $route_id }, _set: $route_update) {
            ...RouteFields
            stopsByRouteId(order_by: { ordering: asc }) {
                ...StopFields
            }
            orders {
                ...OrderRouteMappings
                order {
                    order_id
                }
            }
        }
    }
`;

// SCALAR-ONLY twin of UPDATE_ROUTE_STOPS: no stop inserts/deletes, no mapping
// or event roots, and update_routes_by_pk deliberately returns RouteFields
// only — no stopsByRouteId/orders snapshot. Safe ONLY when route membership
// and stop ordering are unchanged: the StopFields rows normalize onto
// existing stop entities, which both stopsByRouteId storage keys already
// reference. A membership/ordering change through this doc leaves both route
// lists stale — use UPDATE_ROUTE_STOPS for those.
export const UPDATE_STOP_SCALARS = gql`
    ${STOP_FIELDS}
    ${ROUTE_FIELDS}
    ${ORDER_FIELDS}

    mutation UpdateStopScalars(
        $route_id: uuid!
        $route_update: routes_set_input!
        $order_updates: [orders_updates!] = []
        $updates: [stops_updates!] = []
    ) {
        update_orders_many(updates: $order_updates) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
        update_stops_many(updates: $updates) {
            affected_rows
            returning {
                ...StopFields
            }
        }
        update_routes_by_pk(pk_columns: { route_id: $route_id }, _set: $route_update) {
            ...RouteFields
        }
    }
`;

export const UPDATE_ROUTE = gql`
    ${ROUTE_FIELDS}

    mutation UpdateRoute($route_id: uuid!, $update: routes_set_input) {
        update_routes_by_pk(pk_columns: { route_id: $route_id }, _set: $update) {
            ...RouteFields
        }
    }
`;

export const UPSERT_ROUTE = gql`
    ${ROUTE_FIELDS}
    ${TEAMMATE_FIELDS}

    mutation UpdateRoute(
        $route: routes_insert_input!
        $route_update_cols: [routes_update_column!]! = []
        $drivers: [drivers_insert_input!]! = []
        $orders: [orders_insert_input!]! = []
    ) {
        insert_routes_one(
            object: $route
            on_conflict: { constraint: routes_pkey, update_columns: $route_update_cols }
        ) {
            ...RouteFields
            teammateByDriverId {
                ...TeammateFields
            }
            teammateByHelperId {
                ...TeammateFields
            }
        }
        insert_drivers(
            objects: $drivers
            on_conflict: { constraint: drivers_order_key_uniq_idx, update_columns: [name, phone, email] }
        ) {
            affected_rows
        }
        insert_orders(objects: $orders, on_conflict: { constraint: orders_pkey, update_columns: [contractor_id] }) {
            affected_rows
            returning {
                order_id
                carrier_id
                contractor_id
            }
        }
    }
`;

export const RESCHEDULE_ROUTE = gql`
    mutation RescheduleRoute(
        $route_id: uuid!
        $pickup_ids: [uuid!] = []
        $dropoff_ids: [uuid!] = []
        $delivery_date: timestamptz
        $reschedule_reason_code: reschedule_reason_code
        $reschedule_reason_notes: String
    ) {
        update_routes_by_pk(
            pk_columns: { route_id: $route_id }
            _set: { scheduled_delivery: $delivery_date, route_color: null, need_to_optimize: true, start_time: null }
        ) {
            route_id
            route_color
            scheduled_delivery
            start_time
            need_to_optimize
            route_number
        }

        update_stops(
            where: { route_id: { _eq: $route_id } }
            _set: {
                del_window_start: null
                del_window_end: null
                original_del_window_start: null
                original_del_window_end: null
                stop_start_time: null
                stop_end_time: null
                driving_time: null
                driving_distance: null
            }
        ) {
            returning {
                stop_id
                del_window_start
                del_window_end
                original_del_window_start
                original_del_window_end
                stop_start_time
                stop_end_time
                driving_time
                driving_distance
            }
        }

        pickups: update_orders(
            where: { order_id: { _in: $pickup_ids } }
            _set: {
                pickup_date: $delivery_date
                reschedule_reason_code: $reschedule_reason_code
                reschedule_reason_notes: $reschedule_reason_notes
            }
        ) {
            returning {
                order_id
                order_number
                pickup_date
            }
        }

        dropoffs: update_orders(
            where: { order_id: { _in: $dropoff_ids } }
            _set: {
                delivery_date: $delivery_date
                del_window_start: null
                del_window_end: null
                original_del_window_start: null
                original_del_window_end: null
                delivery_time_confirmed: null
                confirmation_source: null
                reschedule_reason_code: $reschedule_reason_code
                reschedule_reason_notes: $reschedule_reason_notes
            }
        ) {
            returning {
                order_id
                order_number
                delivery_date
                del_window_start
                del_window_end
                original_del_window_start
                original_del_window_end
                delivery_time_confirmed
                confirmation_source
            }
        }
    }
`;

export const UNLOCK_ROUTE = gql`
    mutation UnlockRoute($route_id: uuid!) {
        update_routes_by_pk(pk_columns: { route_id: $route_id }, _set: { status: "planning" }) {
            route_id
            route_number
            status
        }
    }
`;

export const REMOVE_ROUTE = gql`
    ${ORDER_FIELDS}
    ${ROUTE_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    mutation RemoveRoute(
        $route_id: uuid!
        $order_updates: [orders_updates!]!
        $events: [order_wh_events_insert_input!]!
        $route: routes_insert_input!
        $mapping_ids_delete: [uuid!] = []
    ) {
        delete_order_route_mappings(where: { mapping_id: { _in: $mapping_ids_delete } }) {
            affected_rows
            returning {
                ...OrderRouteMappings
            }
        }
        insert_routes_one(object: $route, on_conflict: { constraint: routes_pkey, update_columns: [status] }) {
            ...RouteFields
        }
        insert_order_wh_events(objects: $events) {
            affected_rows
            returning {
                ...WarehouseEvents
            }
        }
        update_orders_many(updates: $order_updates) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
        delete_stops(where: { route_id: { _eq: $route_id } }) {
            returning {
                stop_id
            }
        }
    }
`;

export const INSERT_ROUTE = gql`
    ${ROUTE_FIELDS}
    ${STOP_FIELDS}
    ${ORDER_FIELDS}

    mutation InsertRoute(
        $shipper_id: uuid!
        $delivery_date: timestamptz
        $route_color: String
        $finish_returns_next_day: Boolean
    ) {
        insert_routes_one(
            object: {
                shipper_id: $shipper_id
                scheduled_delivery: $delivery_date
                route_color: $route_color
                status: "planning"
                source_form: "PLANNING"
                need_to_optimize: true
                planning: true
                finish_returns_next_day: $finish_returns_next_day
            }
        ) {
            ...RouteFields
            stopsByRouteId {
                ...StopFields
            }
        }
    }
`;

export const INSERT_MULTIPLE_ROUTES = gql`
    ${ROUTE_FIELDS}
    ${STOP_FIELDS}
    ${ORDER_FIELDS}
    ${ROUTE_ORDER_FIELDS}
    mutation InsertMultipleRoutes($routes: [routes_insert_input!]!) {
        insert_routes(objects: $routes) {
            returning {
                ...RouteFields
                ...RouteOrderFields
                stopsByRouteId {
                    ...StopFields
                }
            }
        }
    }
`;

export const NEW_ROUTE_FRAGMENT = gql`
    ${ROUTE_FIELDS}
    ${ORDER_FIELDS}
    ${STOP_FIELDS}

    fragment NewRoute on routes {
        ...RouteFields
        stopsByRouteId {
            ...StopFields
        }
    }
`;
export const CLAIM_LOADS = gql`
    ${ORDER_FIELDS}
    mutation UpdateOrders($order_ids: [uuid!]!, $user_id: uuid!, $delivery_date: timestamptz!) {
        update_orders_many(
            updates: {
                where: { order_id: { _in: $order_ids }, carrier_id: { _is_null: true } }
                _set: { carrier_id: $user_id, order_status: "claimed", delivery_date: $delivery_date, planning: true }
            }
        ) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
    }
`;

export const SEND_TO_ONWARD = gql`
    ${ORDER_FIELDS}
    mutation SendToOnward($order_ids: [uuid!]!) {
        update_orders_many(
            updates: {
                where: { order_id: { _in: $order_ids } }
                _set: { carrier_id: null, order_status: "pending", oms: false, planning: false }
            }
        ) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
    }
`;

export const UPDATE_SETTINGS = gql`
    ${CLIENT_FIELDS}
    mutation UpdateClientSettings($client_id: uuid!, $update: clients_set_input) {
        update_clients_by_pk(pk_columns: { client_id: $client_id }, _set: $update) {
            ...ClientFields
        }
    }
`;

export const UPDATE_CLIENT_DISPATCH_SETTINGS_DEFAULT = gql`
    ${CLIENT_DISPATCH_SETTINGS_FIELDS}
    mutation UpdateClientDispatchSettingsDefault(
        $client_id: uuid!
        $preferences_delivery_timeframe: Int
        $preferences_timeframe_buffer: Int
        $preferences_delivery_window_floor: jsonb
        $preferences_delivery_window_ceiling: jsonb
        $preferences_timeframe_round: Boolean
        $preferences_timeframe_round_minute: Int
        $preferences_timeframe_buffer_apply: Boolean
        $preferences_service_time: Int
        $preferences_service_time_curbside: Int
        $preferences_service_time_room_of_choice: Int
        $preferences_service_time_incr: Int
        $preferences_service_time_first_dry_space: Int
        $preferences_service_time_white_glove: Int
        $preferences_use_service_default_as_minimum: Boolean
        $preferences_default_start_time: jsonb
        $preferences_require_staging_verification: Boolean
    ) {
        update_client_dispatch_settings(
            where: { _and: [{ client_id: { _eq: $client_id } }, { partner_client_id: { _is_null: true } }] }
            _set: {
                preferences_delivery_timeframe: $preferences_delivery_timeframe
                preferences_timeframe_buffer: $preferences_timeframe_buffer
                preferences_delivery_window_floor: $preferences_delivery_window_floor
                preferences_delivery_window_ceiling: $preferences_delivery_window_ceiling
                preferences_timeframe_round: $preferences_timeframe_round
                preferences_timeframe_round_minute: $preferences_timeframe_round_minute
                preferences_timeframe_buffer_apply: $preferences_timeframe_buffer_apply
                preferences_service_time: $preferences_service_time
                preferences_service_time_curbside: $preferences_service_time_curbside
                preferences_service_time_room_of_choice: $preferences_service_time_room_of_choice
                preferences_service_time_incr: $preferences_service_time_incr
                preferences_service_time_first_dry_space: $preferences_service_time_first_dry_space
                preferences_service_time_white_glove: $preferences_service_time_white_glove
                preferences_use_service_default_as_minimum: $preferences_use_service_default_as_minimum
                preferences_default_start_time: $preferences_default_start_time
                preferences_require_staging_verification: $preferences_require_staging_verification
            }
        ) {
            affected_rows
            returning {
                ...ClientDispatchSettingsFields
            }
        }
    }
`;

export const UPDATE_CLIENT_DISPATCH_SETTINGS_PARTNER = gql`
    ${CLIENT_DISPATCH_SETTINGS_FIELDS}
    mutation UpdateClientDispatchSettingsPartner(
        $client_id: uuid!
        $partner_client_id: uuid!
        $preferences_delivery_timeframe: Int
        $preferences_timeframe_buffer: Int
        $preferences_delivery_window_floor: jsonb
        $preferences_delivery_window_ceiling: jsonb
        $preferences_timeframe_round: Boolean
        $preferences_timeframe_round_minute: Int
        $preferences_timeframe_buffer_apply: Boolean
        $preferences_service_time: Int
        $preferences_service_time_curbside: Int
        $preferences_service_time_room_of_choice: Int
        $preferences_service_time_incr: Int
        $preferences_service_time_first_dry_space: Int
        $preferences_service_time_white_glove: Int
        $preferences_use_service_default_as_minimum: Boolean
        $preferences_default_start_time: jsonb
        $preferences_require_staging_verification: Boolean
    ) {
        update_client_dispatch_settings(
            where: { _and: [{ client_id: { _eq: $client_id } }, { partner_client_id: { _eq: $partner_client_id } }] }
            _set: {
                preferences_delivery_timeframe: $preferences_delivery_timeframe
                preferences_timeframe_buffer: $preferences_timeframe_buffer
                preferences_delivery_window_floor: $preferences_delivery_window_floor
                preferences_delivery_window_ceiling: $preferences_delivery_window_ceiling
                preferences_timeframe_round: $preferences_timeframe_round
                preferences_timeframe_round_minute: $preferences_timeframe_round_minute
                preferences_timeframe_buffer_apply: $preferences_timeframe_buffer_apply
                preferences_service_time: $preferences_service_time
                preferences_service_time_curbside: $preferences_service_time_curbside
                preferences_service_time_room_of_choice: $preferences_service_time_room_of_choice
                preferences_service_time_incr: $preferences_service_time_incr
                preferences_service_time_first_dry_space: $preferences_service_time_first_dry_space
                preferences_service_time_white_glove: $preferences_service_time_white_glove
                preferences_use_service_default_as_minimum: $preferences_use_service_default_as_minimum
                preferences_default_start_time: $preferences_default_start_time
                preferences_require_staging_verification: $preferences_require_staging_verification
            }
        ) {
            affected_rows
            returning {
                ...ClientDispatchSettingsFields
            }
        }
    }
`;

export const INSERT_CLIENT_DISPATCH_SETTINGS_DEFAULT = gql`
    ${CLIENT_DISPATCH_SETTINGS_FIELDS}
    mutation InsertClientDispatchSettingsDefault(
        $client_id: uuid!
        $preferences_delivery_timeframe: Int
        $preferences_timeframe_buffer: Int
        $preferences_delivery_window_floor: jsonb
        $preferences_delivery_window_ceiling: jsonb
        $preferences_timeframe_round: Boolean
        $preferences_timeframe_round_minute: Int
        $preferences_timeframe_buffer_apply: Boolean
        $preferences_service_time: Int
        $preferences_service_time_curbside: Int
        $preferences_service_time_room_of_choice: Int
        $preferences_service_time_incr: Int
        $preferences_service_time_first_dry_space: Int
        $preferences_service_time_white_glove: Int
        $preferences_use_service_default_as_minimum: Boolean
        $preferences_default_start_time: jsonb
        $preferences_require_staging_verification: Boolean
    ) {
        insert_client_dispatch_settings_one(
            object: {
                client_id: $client_id
                partner_client_id: null
                preferences_delivery_timeframe: $preferences_delivery_timeframe
                preferences_timeframe_buffer: $preferences_timeframe_buffer
                preferences_delivery_window_floor: $preferences_delivery_window_floor
                preferences_delivery_window_ceiling: $preferences_delivery_window_ceiling
                preferences_timeframe_round: $preferences_timeframe_round
                preferences_timeframe_round_minute: $preferences_timeframe_round_minute
                preferences_timeframe_buffer_apply: $preferences_timeframe_buffer_apply
                preferences_service_time: $preferences_service_time
                preferences_service_time_curbside: $preferences_service_time_curbside
                preferences_service_time_room_of_choice: $preferences_service_time_room_of_choice
                preferences_service_time_incr: $preferences_service_time_incr
                preferences_service_time_first_dry_space: $preferences_service_time_first_dry_space
                preferences_service_time_white_glove: $preferences_service_time_white_glove
                preferences_use_service_default_as_minimum: $preferences_use_service_default_as_minimum
                preferences_default_start_time: $preferences_default_start_time
                preferences_require_staging_verification: $preferences_require_staging_verification
            }
        ) {
            ...ClientDispatchSettingsFields
        }
    }
`;

export const INSERT_CLIENT_DISPATCH_SETTINGS_PARTNER = gql`
    ${CLIENT_DISPATCH_SETTINGS_FIELDS}
    mutation InsertClientDispatchSettingsPartner(
        $client_id: uuid!
        $partner_client_id: uuid!
        $preferences_delivery_timeframe: Int
        $preferences_timeframe_buffer: Int
        $preferences_delivery_window_floor: jsonb
        $preferences_delivery_window_ceiling: jsonb
        $preferences_timeframe_round: Boolean
        $preferences_timeframe_round_minute: Int
        $preferences_timeframe_buffer_apply: Boolean
        $preferences_service_time: Int
        $preferences_service_time_curbside: Int
        $preferences_service_time_room_of_choice: Int
        $preferences_service_time_incr: Int
        $preferences_service_time_first_dry_space: Int
        $preferences_service_time_white_glove: Int
        $preferences_use_service_default_as_minimum: Boolean
        $preferences_default_start_time: jsonb
        $preferences_require_staging_verification: Boolean
    ) {
        insert_client_dispatch_settings_one(
            object: {
                client_id: $client_id
                partner_client_id: $partner_client_id
                preferences_delivery_timeframe: $preferences_delivery_timeframe
                preferences_timeframe_buffer: $preferences_timeframe_buffer
                preferences_delivery_window_floor: $preferences_delivery_window_floor
                preferences_delivery_window_ceiling: $preferences_delivery_window_ceiling
                preferences_timeframe_round: $preferences_timeframe_round
                preferences_timeframe_round_minute: $preferences_timeframe_round_minute
                preferences_timeframe_buffer_apply: $preferences_timeframe_buffer_apply
                preferences_service_time: $preferences_service_time
                preferences_service_time_curbside: $preferences_service_time_curbside
                preferences_service_time_room_of_choice: $preferences_service_time_room_of_choice
                preferences_service_time_incr: $preferences_service_time_incr
                preferences_service_time_first_dry_space: $preferences_service_time_first_dry_space
                preferences_service_time_white_glove: $preferences_service_time_white_glove
                preferences_use_service_default_as_minimum: $preferences_use_service_default_as_minimum
                preferences_default_start_time: $preferences_default_start_time
                preferences_require_staging_verification: $preferences_require_staging_verification
            }
        ) {
            ...ClientDispatchSettingsFields
        }
    }
`;
